// mui
import { Grid, Typography } from '@mui/material';
// notistack
import { enqueueSnackbar } from 'notistack';
// form
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
// little-state-machine
import { useStateMachine } from 'little-state-machine';
// component
import { FormProvider, RHFTextField } from '../../../../components/hook-form';
import FormSectionControls from '../../FormSectionControls';
// hooks
import useFormStateUpdateOnChange from '../../../../hooks/useFormStateUpdateOnChange';
// services
import formValidationSchema from '../../../../services/validation/formValidationSchema';

// -------------------------------------------------------

const Form = () => {
  // state
  const { state } = useStateMachine();

  // form
  const methods = useForm({
    mode: 'onBlur',
    resolver: yupResolver(formValidationSchema),
    defaultValues: state.form,
  });

  const { watch } = methods;

  useFormStateUpdateOnChange(watch);

  const validate = async () => {
    let isValid = true;

    try {
      await formValidationSchema.validate(methods.getValues(), { abortEarly: false });
    } catch (err) {
      isValid = false;
      const firstErrors = err.inner.reduce((acc, currentError) => {
        if (!Object.prototype.hasOwnProperty.call(acc, currentError.path)) {
          acc[currentError.path] = currentError.message;
        }
        return acc;
      }, {});

      const errorsArray = Object.entries(firstErrors).map(([path, message]) => ({ path, message }));
      
      errorsArray.forEach(element => {
        const tabPathName = element.path.match(/^[^.[]+/)[0];

        let tabName = '';
        switch (tabPathName) {
          case 'insured':
            tabName = 'Insured';
            break;
          case 'locations':
            tabName = 'Locations';
            break;
          case 'coverageAndClasses':
            tabName = 'Coverage And Classes';
            break;
          case 'ownersOfficers':
            tabName = 'Owners / Officers';
            break;
          case 'currentCoverage':
            tabName = 'Current Coverage';
            break;
          case 'additionalQuestions':
            tabName = 'Additional Questions';
            break;
          default:
            tabName = 'current';
            break;
        }

        enqueueSnackbar(`Please correct the following error: ${element.message} in ${tabName} tab`, { variant: 'error' });
      });
    }

    return isValid;
  };

  return (
    <FormProvider methods={methods}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography>
            PERSONAL INFORMATION ABOUT YOU, INCLUDING INFORMATION FROM A CREDIT OR OTHER INVESTIGATIVE REPORT, 
            MAY BE COLLECTED FROM PERSONS OTHER THAN YOU IN CONNECTION WITH THIS APPLICATION FOR INSURANCE AND 
            SUBSEQUENT AMENDMENTS AND RENEWALS. SUCH INFORMATION AS WELL AS OTHER PERSONAL AND PRIVILEGED 
            INFORMATION COLLECTED BY US OR OUR AGENTS MAY IN CERTAIN CIRCUMSTANCES BE DISCLOSED TO THIRD 
            PARTIES WITHOUT YOUR AUTHORIZATION. CREDIT SCORING INFORMATION MAY BE USED TO HELP DETERMINE 
            EITHER YOUR ELIGIBILITY FOR INSURANCE OR THE PREMIUM YOU WILL BE CHARGED. WE MAY USE A THIRD 
            PARTY IN CONNECTION WITH THE DEVELOPMENT OF YOUR SCORE. YOU MAY HAVE THE RIGHT TO REVIEW YOUR 
            PERSONAL INFORMATION IN OUR FILES AND REQUEST CORRECTION OF ANY INACCURACIES. YOU MAY ALSO HAVE 
            THE RIGHT TO REQUEST IN WRITING THAT WE CONSIDER EXTRAORDINARY LIFE CIRCUMSTANCES IN CONNECTION 
            WITH THE DEVELOPMENT OF YOUR CREDIT SCORE. THESE RIGHTS MAYBE LIMITED IN SOME STATES. PLEASE 
            CONTACT YOUR AGENT OR BROKER TO LEARN HOW THESE RIGHTS MAY APPLY IN YOUR STATE OR FOR INSTRUCTIONS 
            ON HOW TO SUBMIT A REQUEST TO US FOR A MORE DETAILED DESCRIPTION OF YOUR RIGHTS AND OUR PRACTICES 
            REGARDING PERSONAL INFORMATION. (Not applicable in AZ, CA, DE, KS, MA, MN, ND, NY, OR, VA, or WV. 
            Specific ACORD 38s are available for applicants in these states.)
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography>
            <strong>Applicable in AL, AR, DC, LA, MD, NM, RI and WV</strong>: Any person who knowingly (or willfully)* 
            presents a false or fraudulent claim for payment of a loss or benefit or knowingly (or willfully)* presents 
            false information in an application for insurance is guilty of a crime and may be subject to fines and 
            confinement in prison. *Applies in MD Only.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography>
              <strong>Applicable in CO</strong>: It is unlawful to knowingly provide false, incomplete, or misleading 
              facts or information to an insurance company for the purpose of defrauding or attempting to defraud the 
              company. Penalties may include imprisonment, fines, denial of insurance and civil damages. Any insurance 
              company or agent of an insurance company who knowingly provides false, incomplete, or misleading facts or
              information to a policyholder or claimant for the purpose of defrauding or attempting to defraud the policyholder 
              or claimant with regard to a settlement or award payable from insurance proceeds shall be reported to the 
              Colorado Division of Insurance within the Department of Regulatory Agencies.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography>
            <strong>Applicable in FL and OK</strong>: Any person who knowingly and with intent to injure, defraud, or deceive 
            any insurer files a statement of claim or an application containing any false, incomplete, or misleading information 
            is guilty of a felony (of the third degree)*. *Applies in FL Only
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography>
            <strong>Applicable in KS</strong>: Any person who, knowingly and with intent to defraud, presents, causes to be presented
            or prepares with knowledge or belief that it will be presented to or by an insurer, purported insurer, broker or 
            any agent thereof, any written, electronic, electronic impulse, facsimile, magnetic, oral, or telephonic communication
            or statement as part of, or in support of, an application for the issuance of, or the rating of an insurance policy 
            for personal or commercial insurance, or a claim for payment or other benefit pursuant to an insurance policy for
            commercial or personal insurance which such person knows to contain materially false information concerning any
            fact material thereto; or conceals, for the purpose of misleading, information concerning any fact material thereto 
            commits a fraudulent insurance act.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography>
            <strong>Applicable in KY, NY, OH and PA</strong>: Any person who knowingly and with intent to defraud any insurance company 
            or other person files an application for insurance or statement of claim containing any materially false information or 
            conceals for the purpose of misleading, information concerning any fact material thereto commits a fraudulent insurance 
            act, which is a crime and subjects such person to criminal and civil penalties (not to exceed five thousand dollars and 
            the stated value of the claim for each such violation)*. *Applies in NY Only.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography>
            <strong>Applicable in ME, TN, VA and WA</strong>: It is a crime to knowingly provide false, incomplete or misleading 
            information to an insurance company for the purpose of defrauding the company. Penalties (may)* include imprisonment, 
            fines and denial of insurance benefits. *Applies in ME Only.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography>
            <strong>Applicable in NJ</strong>: Any person who includes any false or misleading information on an application for 
            an insurance policy is subject to criminal and civil penalties.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography>
            <strong>Applicable in OR</strong>: Any person who knowingly and with intent to defraud or solicit another to defraud the
            insurer by submitting an application containing a false statement as to any material fact may be violating state law.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography>
            <strong>Applicable in PR</strong>: Any person who knowingly and with the intention of defrauding presents false information
            in an insurance application, or presents, helps, or causes the presentation of a fraudulent claim for the payment of a loss 
            or any other benefit, or presents more than one claim for the same damage or loss, shall incur a felony and, upon conviction,
            shall be sanctioned for each violation by a fine of not less than five thousand dollars ($5,000) and not more than ten 
            thousand dollars ($10,000), or a fixed term of imprisonment for three (3) years, or both penalties. Should aggravating 
            circumstances [be] present, the penalty thus established may be increased to a maximum of five (5) years, if extenuating 
            circumstances are present, it may be reduced to a minimum of two (2) years.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography>
            <strong>Applicable in UT</strong>: Any person who knowingly presents false or fraudulent underwriting information, files or 
            causes to be filed a false or fraudulent claim for disability compensation or medical benefits, or submits a false or fraudulent
            report or billing for health care fees or other professional services is guilty of a crime and may be subject to fines and 
            confinement in state prison.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography>
              THE UNDERSIGNED IS AN AUTHORIZED REPRESENTATIVE OF THE APPLICANT AND REPRESENTS THAT REASONABLE INQUIRY HAS BEEN MADE TO
              OBTAIN THEANSWERS TO QUESTIONS ON THIS APPLICATION. HE/SHE REPRESENTS THAT THE ANSWERS ARE TRUE, CORRECT AND COMPLETE TO
              THE BEST OF HIS/HER KNOWLEDGE.</Typography>
        </Grid>

        <Grid item xs={12} sm={4} md={3} mt={1}>
          <RHFTextField name={'policyOwnershipAndFraudStatement.applicantName'} label={'Applicant Name'} required />
        </Grid>
        <Grid item xs={12} sm={4} md={3} mt={1}>
          <RHFTextField name={'policyOwnershipAndFraudStatement.email'} label={'Email'} required />
        </Grid>
        <Grid item xs={12} sm={4} md={3} mt={1}>
          <RHFTextField name={'policyOwnershipAndFraudStatement.mailingAddress'} label={'Mailing Address'} />
        </Grid>
        <Grid item xs={12} sm={4} md={3} mt={1}>
          <RHFTextField name={'policyOwnershipAndFraudStatement.officePhone'} label={'Office Phone'} />
        </Grid>
        <Grid item xs={12} sm={4} md={3} mt={1}>
          <RHFTextField name={'policyOwnershipAndFraudStatement.phone'} label={'Phone'} />
        </Grid>
        <Grid item xs={12} sm={4} md={3} mt={1}>
          <RHFTextField name={'policyOwnershipAndFraudStatement.referralSource'} label={'Who referred you to McGriff?'} />
        </Grid>
        <Grid item xs={12} sm={12} md={3} mt={1}/>
        <Grid item xs={12} sm={12} md={3} mt={1} container alignItems={'center'} justifyContent={'flex-end'}>
          <Typography>McGriff CA License #0C64544</Typography>
        </Grid>
      </Grid>

      <FormSectionControls step={'policy-ownership-and-fraud-statement'} validate={validate} />
    </FormProvider>
  );
};

export default Form;
